import React, { useEffect, useState } from 'react';

import { cssMap } from '@compiled/react';
import { token } from '@atlaskit/tokens';

import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import Form from '@atlaskit/form/form';
import { FormHeader } from '@atlaskit/form/form-header';

import { Text, Stack } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';

import { useIntl, defineMessage } from '@atlassian/jira-intl';

import { lookupTypeByTypeId } from './TypeDefinition.types.tsx';
import ProductCard from './ProductCard.tsx';
import {
	autoConfigureApplinkConsumer,
	fetchApplinkStatus,
	putApplinkOAuthOne2LOProvider,
	putApplinkOAuthOne3LOProvider,
} from './fetchApplinkManifestForUrl.tsx';
import type { ApplicationLinkV3Status } from './CommonApplicationLinkCreationEntity.tsx';
import { APPLINKS_LIST_PAGE_URL } from './applinksListPageUrl.constants.tsx';
import type { StageConfirmationProps } from './StageProps.types.tsx';
import { StatusLozengeV3 } from './StatusLozenge.tsx';
import { SubpageTemplate } from './SubpageTemplate.tsx';

const styles = cssMap({
	linkDisplay: {
		marginTop: token('space.200'),
		marginBottom: token('space.200'),
	},
	fullContainer: {
		height: '100%',
	},
});

const doneButton = defineMessage({
	id: 'applinks.administration.list.creation-flow-dialog.confirmation.done-button.non-final-ai',
	defaultMessage: 'Done',
	description:
		'The text is shown as a button label when the user completes the application link creation flow. Used in the confirmation dialog to submit and finish the setup process.',
});

const applicationLinkCreated = defineMessage({
	id: 'applinks.administration.list.creation-flow-dialog.confirmation.application-link-created.non-final-ai',
	defaultMessage: 'The application link has been created.',
	description:
		'The text is shown as a confirmation message after successfully creating an application link. Displayed in the confirmation dialog to inform users that the link setup is complete.',
});

type ReviewSubpageProps = {
	state: StageConfirmationProps;
};

const ConfirmationSubpage = (props: ReviewSubpageProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const [status, setStatus] = useState<ApplicationLinkV3Status | null>(null);

	const finaliseOAuth1Link = async (confirmationApplinkId: string) => {
		await putApplinkOAuthOne3LOProvider(confirmationApplinkId);
		await autoConfigureApplinkConsumer(confirmationApplinkId);
		await putApplinkOAuthOne2LOProvider(confirmationApplinkId);

		setStatus(await fetchApplinkStatus(confirmationApplinkId));
	};

	// eslint-disable-next-line react-you-might-not-need-an-effect/no-reset-all-state-on-prop-change
	useEffect(() => {
		// TODO: Enforce this using discriminated union types
		if (props.state.variant === 'OAUTH2.0') {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
			setStatus(null);
			return;
		}
		finaliseOAuth1Link(props.state.confirmationApplinkId);
	}, [props.state.confirmationApplinkId, props.state.variant]);

	return (
		<Form
			onSubmit={() => {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.location.href = APPLINKS_LIST_PAGE_URL;
			}}
			xcss={styles.fullContainer}
		>
			<SubpageTemplate
				variant={props.state.variant}
				kind={props.state.kind}
				buttons={
					<ButtonGroup label="Form submit options">
						<Button type="submit" appearance="primary" isLoading={status === null}>
							{formatMessage(doneButton)}
						</Button>
					</ButtonGroup>
				}
				content={
					<Stack>
						<FormHeader title="Confirmation">
							<Text>{formatMessage(applicationLinkCreated)}</Text>
						</FormHeader>

						<Stack xcss={styles.linkDisplay} space="space.200">
							{status && (
								<ProductCard
									// TODO: Determine if the type is DC or Cloud
									applicationType={lookupTypeByTypeId(status.link.type, false)}
									displayUrl={status.link.displayUrl}
									heading="Application Link"
									statusLozenge={<StatusLozengeV3 status={status} />}
								/>
							)}
							{status === null && <Spinner size="medium" />}
						</Stack>
					</Stack>
				}
			/>
		</Form>
	);
};

export default ConfirmationSubpage;
